// Compensation derivation for the Personnel File.
// Reads from the immutable payroll ledger + assignments. Nothing writable.
import { supabase } from "@/integrations/supabase/client";

export type Compensation = {
  ytdCents: number;
  lifetimeCents: number;
  currentSemesterCents: number;
  avgMonthlyCents: number;
  paymentEntries: number;
  firstPaymentAt: string | null;
  lastPaymentAt: string | null;
  currentSemester: "spring" | "summer" | "fall";
  taxYear: number;
  upcomingPayrollCents: number | null;
};

function currentSemester(d: Date): "spring" | "summer" | "fall" {
  const m = d.getMonth() + 1;
  if (m <= 4) return "spring";
  if (m <= 8) return "summer";
  return "fall";
}

export async function fetchInstructorCompensation(instructorId: string): Promise<Compensation> {
  const now = new Date();
  const year = now.getFullYear();
  const sem = currentSemester(now);

  const { data, error } = await (supabase.from as any)("payroll_ledger")
    .select("final_cents, paid_at, semester")
    .eq("instructor_id", instructorId)
    .is("voided_at", null)
    .order("paid_at", { ascending: true });
  if (error) throw error;
  const rows: { final_cents: number; paid_at: string; semester: string | null }[] = data ?? [];

  let ytd = 0;
  let lifetime = 0;
  let semTotal = 0;
  for (const r of rows) {
    lifetime += r.final_cents ?? 0;
    const paid = new Date(r.paid_at);
    if (paid.getFullYear() === year) ytd += r.final_cents ?? 0;
    if (paid.getFullYear() === year && (r.semester ?? currentSemester(paid)) === sem) {
      semTotal += r.final_cents ?? 0;
    }
  }
  const first = rows[0]?.paid_at ?? null;
  const last = rows.length ? rows[rows.length - 1].paid_at : null;
  const monthsActive = first
    ? Math.max(1, Math.round((now.getTime() - new Date(first).getTime()) / (30 * 24 * 60 * 60 * 1000)))
    : 1;

  // Upcoming payroll: not confidently derivable without period assumptions — leave null.
  return {
    ytdCents: ytd,
    lifetimeCents: lifetime,
    currentSemesterCents: semTotal,
    avgMonthlyCents: Math.round(lifetime / monthsActive),
    paymentEntries: rows.length,
    firstPaymentAt: first,
    lastPaymentAt: last,
    currentSemester: sem,
    taxYear: year,
    upcomingPayrollCents: null,
  };
}

export const centsToUsd = (c: number) => `$${(c / 100).toFixed(2)}`;
